import { Button } from '@/components/ui/Button';
import {
  useManualVaultSync,
  useLocalVaultUpload,
} from '@/hooks/useVaultSyncConfiguration';
import { useEmployees } from '@/features/employees/hooks/useEmployees';
import { vaultService } from '@/services/vault.service';
import { Vault, VaultStatus } from '@/types/vault.types';
import { Employee } from '@/features/employees/types';
import { useQuery } from '@tanstack/react-query';
import {
  Upload,
  FileText,
  FileBarChart,
  Calendar,
  AlertCircle,
  Loader2,
  Building2,
  Users,
  User,
  Search,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Cloud,
  HardDrive,
  File,
  X,
  UserCheck,
  ArrowRightLeft,
} from 'lucide-react';
import { useState, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface EmployeeVaultInfo {
  vault: Vault;
  employeeName: string;
  employeeMatricule: string;
}

interface VaultSyncTabProps {
  folderUuid: string;
  folderName: string;
  domainUuid?: string;
  folderVault: Vault | null | undefined;
}

type DepositMode = 'grouped' | 'individual';
type SourceMode = 'api' | 'local';

const DOCUMENT_TYPES = [
  { value: 'payslip', label: 'Bulletin de paie' },
  { value: 'contract', label: 'Contrat de travail' },
  { value: 'certificate', label: 'Attestation' },
  { value: 'edition', label: 'Édition' },
  { value: 'other', label: 'Autre' },
];

const buildLocalizedMonths = (locale: string) => {
  return Array.from({ length: 12 }, (_, idx) => {
    const monthNumber = idx + 1;
    const date = new Date(2000, idx, 1);
    const label = date.toLocaleString(locale || 'fr-FR', { month: 'long' });
    const capitalized = label.charAt(0).toUpperCase() + label.slice(1);
    return { value: String(monthNumber), label: capitalized };
  });
};

const YEARS = Array.from({ length: 3 }, (_, i) => {
  const year = new Date().getFullYear() - i;
  return { value: year.toString(), label: year.toString() };
});

export function VaultSyncTab({
  folderUuid,
  folderName,
  domainUuid: _domainUuid,
  folderVault,
}: VaultSyncTabProps) {
  const { t, i18n } = useTranslation();
  const MONTHS = useMemo(
    () => buildLocalizedMonths(i18n.language),
    [i18n.language]
  );

  // Source mode
  const [sourceMode, setSourceMode] = useState<SourceMode>('api');

  // Deposit mode (for API mode)
  const [depositMode, setDepositMode] = useState<DepositMode>('grouped');

  // Manual sync state (API mode)
  const [syncPayslips, setSyncPayslips] = useState(true);
  const [syncDsn, setSyncDsn] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(
    (new Date().getMonth() + 1).toString()
  );
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString()
  );

  // Direct deposit toggle (for API grouped mode)
  const [directDepositToEmployee, setDirectDepositToEmployee] = useState(true);

  // Employee selection state (for individual/local mode)
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [employeeSearch, setEmployeeSearch] = useState('');

  // Local upload state
  const [localFile, setLocalFile] = useState<File | null>(null);
  const [localDocType, setLocalDocType] = useState('payslip');
  const [localMonth, setLocalMonth] = useState(
    (new Date().getMonth() + 1).toString()
  );
  const [localYear, setLocalYear] = useState(
    new Date().getFullYear().toString()
  );
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch employees
  const { data: employeesData, isLoading: isLoadingEmployees } = useEmployees(
    folderUuid,
    { limit: 100 }
  );

  // Fetch employee vaults with their status
  const { data: employeeVaultsMap } = useQuery({
    queryKey: ['vault', 'folder-employee-vaults-full', folderUuid],
    queryFn: async () => {
      try {
        const response = await vaultService.getFolderEmployeeVaults(folderUuid);
        const data = response?.data;
        const vaults = Array.isArray(data) ? data : [];
        const vaultMap = new Map<string, EmployeeVaultInfo>();
        vaults.forEach(v => {
          if (v.vault?.entityId) {
            vaultMap.set(v.vault.entityId, v);
          }
        });
        return vaultMap;
      } catch (error) {
        console.error('Error fetching employee vaults:', error);
        return new Map<string, EmployeeVaultInfo>();
      }
    },
    enabled: !!folderUuid,
  });

  const { mutate: executeSync, isPending: isSyncing } = useManualVaultSync();
  const { mutate: uploadLocal, isPending: isUploading } = useLocalVaultUpload();

  // Get vault info for an employee
  const getEmployeeVaultInfo = useCallback(
    (employeeUuid: string): EmployeeVaultInfo | undefined =>
      employeeVaultsMap?.get(employeeUuid),
    [employeeVaultsMap]
  );

  // Check if employee vault is active
  const isVaultActive = (employeeUuid: string): boolean => {
    const vaultInfo = getEmployeeVaultInfo(employeeUuid);
    return vaultInfo?.vault?.status === VaultStatus.ACTIVE;
  };

  // Check if employee has any vault
  const hasEmployeeVault = (employeeUuid: string): boolean => {
    return employeeVaultsMap?.has(employeeUuid) || false;
  };

  // Count employees with non-active vaults among selected
  const selectedWithInactiveVaults = useMemo(() => {
    return selectedEmployees.filter(id => {
      const vaultInfo = getEmployeeVaultInfo(id);
      return !vaultInfo || vaultInfo.vault?.status !== VaultStatus.ACTIVE;
    });
  }, [selectedEmployees, getEmployeeVaultInfo]);

  // Count total employees without active vaults
  const employeesWithoutActiveVaults = useMemo(() => {
    if (!employeesData?.data) return [];
    return employeesData.data.filter((emp: Employee) => {
      const vaultInfo = getEmployeeVaultInfo(emp.uuid);
      return !vaultInfo || vaultInfo.vault?.status !== VaultStatus.ACTIVE;
    });
  }, [employeesData?.data, getEmployeeVaultInfo]);

  const filteredEmployees = useMemo((): Employee[] => {
    if (!employeesData?.data) return [];
    if (!employeeSearch.trim()) return employeesData.data;
    const search = employeeSearch.toLowerCase();
    return employeesData.data.filter(
      (emp: Employee) =>
        emp.prenom?.toLowerCase().includes(search) ||
        emp.nom?.toLowerCase().includes(search) ||
        emp.matriculeSalarie?.toLowerCase().includes(search)
    );
  }, [employeesData?.data, employeeSearch]);

  const handleEmployeeToggle = (employeeUuid: string) => {
    setSelectedEmployees(prev =>
      prev.includes(employeeUuid)
        ? prev.filter(id => id !== employeeUuid)
        : [...prev, employeeUuid]
    );
  };

  const handleSelectAllEmployees = () => {
    if (!employeesData?.data) return;
    if (selectedEmployees.length === employeesData.data.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employeesData.data.map((emp: Employee) => emp.uuid));
    }
  };

  // --- Drag & drop handlers ---
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setLocalFile(files[0]);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setLocalFile(e.target.files[0]);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // --- API mode sync ---
  const handleManualSync = () => {
    if (!syncPayslips && !syncDsn) {
      toast.error(
        t(
          'vault.sync.selectDocument',
          'Veuillez sélectionner au moins un type de document'
        )
      );
      return;
    }

    if (!folderVault) {
      toast.error(
        t(
          'vault.sync.noVault',
          "Veuillez d'abord activer le coffre-fort du dossier"
        )
      );
      return;
    }

    if (depositMode === 'individual' && selectedEmployees.length === 0) {
      toast.error(
        t(
          'vault.manualDeposit.selectEmployees',
          'Veuillez sélectionner au moins un employé'
        )
      );
      return;
    }

    executeSync(
      {
        folderUuid,
        syncPayslips,
        syncDsn,
        year: parseInt(selectedYear),
        month: parseInt(selectedMonth),
        depositMode,
        employeeUuids:
          depositMode === 'individual' ? selectedEmployees : undefined,
        directDepositToEmployee:
          depositMode === 'grouped' ? directDepositToEmployee : undefined,
      },
      {
        onSuccess: result => {
          if (result.success) {
            toast.success(
              t('vault.sync.success', 'Dépôt effectué avec succès') +
                ` (${result.details.documentsCreated} document(s))`
            );
          } else if (result.errors?.length > 0) {
            toast.error(result.errors[0]);
          } else {
            toast.warning(t('vault.sync.noDocuments', 'Aucun document déposé'));
          }
          if (depositMode === 'individual') {
            setSelectedEmployees([]);
          }
        },
      }
    );
  };

  // --- Local mode upload ---
  const handleLocalUpload = () => {
    if (!localFile) {
      toast.error(
        t('vault.localUpload.noFile', 'Veuillez sélectionner un fichier')
      );
      return;
    }

    if (!selectedEmployee) {
      toast.error(
        t('vault.localUpload.noEmployee', 'Veuillez sélectionner un employé')
      );
      return;
    }

    if (!folderVault) {
      toast.error(
        t(
          'vault.sync.noVault',
          "Veuillez d'abord activer le coffre-fort du dossier"
        )
      );
      return;
    }

    const employee = employeesData?.data?.find(
      (e: Employee) => e.uuid === selectedEmployee
    );

    uploadLocal(
      {
        folderUuid,
        employeeUuid: selectedEmployee,
        file: localFile,
        documentType: localDocType,
        year: parseInt(localYear),
        month: parseInt(localMonth),
        employeeNumber: employee?.matriculeSalarie || undefined,
      },
      {
        onSuccess: () => {
          setLocalFile(null);
          setSelectedEmployee('');
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        },
      }
    );
  };

  const hasVault = !!folderVault;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
          <Upload className="h-5 w-5 text-purple-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-neutral-900">
            {t('vault.manualDeposit.title', 'Dépôt Manuel')}
          </h3>
          <p className="text-sm text-neutral-500">
            {t(
              'vault.manualDeposit.description',
              'Déposez manuellement des documents vers les coffres-forts'
            )}
          </p>
        </div>
      </div>

      {!hasVault && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">
                {t('vault.sync.noVaultWarning', 'Coffre-fort non activé')}
              </p>
              <p className="mt-1 text-sm text-amber-700">
                {t(
                  'vault.sync.noVaultDescription',
                  "Veuillez d'abord activer le coffre-fort du dossier dans l'onglet Gestion pour pouvoir déposer des documents."
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      <div
        className={`space-y-6 ${!hasVault ? 'pointer-events-none opacity-50' : ''}`}
      >
        {/* Source Mode Selection */}
        <div>
          <label className="mb-3 block text-sm font-medium text-neutral-700">
            {t('vault.manualDeposit.source', 'Source des documents')}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label
              className={`flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-all ${
                sourceMode === 'api'
                  ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500'
                  : 'border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              <input
                type="radio"
                name="sourceMode"
                checked={sourceMode === 'api'}
                onChange={() => setSourceMode('api')}
                className="sr-only"
              />
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                  sourceMode === 'api' ? 'bg-purple-100' : 'bg-neutral-100'
                }`}
              >
                <Cloud
                  className={`h-5 w-5 ${
                    sourceMode === 'api'
                      ? 'text-purple-600'
                      : 'text-neutral-500'
                  }`}
                />
              </div>
              <div>
                <p
                  className={`font-medium ${
                    sourceMode === 'api'
                      ? 'text-purple-900'
                      : 'text-neutral-900'
                  }`}
                >
                  {t('vault.manualDeposit.sourceApi', 'API OpenPaye')}
                </p>
                <p className="text-xs text-neutral-500">
                  {t(
                    'vault.manualDeposit.sourceApiDesc',
                    'Récupération automatique'
                  )}
                </p>
              </div>
            </label>

            <label
              className={`flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-all ${
                sourceMode === 'local'
                  ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500'
                  : 'border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              <input
                type="radio"
                name="sourceMode"
                checked={sourceMode === 'local'}
                onChange={() => setSourceMode('local')}
                className="sr-only"
              />
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                  sourceMode === 'local' ? 'bg-purple-100' : 'bg-neutral-100'
                }`}
              >
                <HardDrive
                  className={`h-5 w-5 ${
                    sourceMode === 'local'
                      ? 'text-purple-600'
                      : 'text-neutral-500'
                  }`}
                />
              </div>
              <div>
                <p
                  className={`font-medium ${
                    sourceMode === 'local'
                      ? 'text-purple-900'
                      : 'text-neutral-900'
                  }`}
                >
                  {t('vault.manualDeposit.sourceLocal', 'Fichier Local')}
                </p>
                <p className="text-xs text-neutral-500">
                  {t(
                    'vault.manualDeposit.sourceLocalDesc',
                    'Téléverser depuis votre PC'
                  )}
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* ============================================ */}
        {/* API OpenPaye Mode                            */}
        {/* ============================================ */}
        {sourceMode === 'api' && (
          <>
            {/* Deposit Mode Selection */}
            <div>
              <label className="mb-3 block text-sm font-medium text-neutral-700">
                {t('vault.manualDeposit.mode', 'Mode de dépôt')}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-all ${
                    depositMode === 'grouped'
                      ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500'
                      : 'border-neutral-200 hover:bg-neutral-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="depositMode"
                    checked={depositMode === 'grouped'}
                    onChange={() => setDepositMode('grouped')}
                    className="sr-only"
                  />
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                      depositMode === 'grouped'
                        ? 'bg-purple-100'
                        : 'bg-neutral-100'
                    }`}
                  >
                    <Building2
                      className={`h-5 w-5 ${
                        depositMode === 'grouped'
                          ? 'text-purple-600'
                          : 'text-neutral-500'
                      }`}
                    />
                  </div>
                  <div>
                    <p
                      className={`font-medium ${
                        depositMode === 'grouped'
                          ? 'text-purple-900'
                          : 'text-neutral-900'
                      }`}
                    >
                      {t('vault.manualDeposit.grouped', 'Groupé')}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {t(
                        'vault.manualDeposit.groupedDescription',
                        'Vers le coffre dossier'
                      )}
                    </p>
                  </div>
                </label>

                <label
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-all ${
                    depositMode === 'individual'
                      ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500'
                      : 'border-neutral-200 hover:bg-neutral-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="depositMode"
                    checked={depositMode === 'individual'}
                    onChange={() => setDepositMode('individual')}
                    className="sr-only"
                  />
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                      depositMode === 'individual'
                        ? 'bg-purple-100'
                        : 'bg-neutral-100'
                    }`}
                  >
                    <Users
                      className={`h-5 w-5 ${
                        depositMode === 'individual'
                          ? 'text-purple-600'
                          : 'text-neutral-500'
                      }`}
                    />
                  </div>
                  <div>
                    <p
                      className={`font-medium ${
                        depositMode === 'individual'
                          ? 'text-purple-900'
                          : 'text-neutral-900'
                      }`}
                    >
                      {t('vault.manualDeposit.individual', 'Individuel')}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {t(
                        'vault.manualDeposit.individualDescription',
                        'Vers les coffres employés'
                      )}
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Document Types */}
            <div>
              <label className="mb-3 block text-sm font-medium text-neutral-700">
                {t('vault.sync.documentsToSync', 'Documents à déposer')}
              </label>
              <div className="space-y-3">
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-neutral-200 p-4 transition-colors hover:bg-neutral-50">
                  <input
                    type="checkbox"
                    checked={syncPayslips}
                    onChange={e => setSyncPayslips(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-neutral-300 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-500" />
                      <span className="font-medium text-neutral-900">
                        {t('vault.sync.payslips', 'Bulletins de paie')}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-neutral-500">
                      {t(
                        'vault.sync.payslipsDescription',
                        'Déposer les bulletins vers les coffres-forts'
                      )}
                    </p>
                  </div>
                </label>

                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-neutral-200 p-4 transition-colors hover:bg-neutral-50">
                  <input
                    type="checkbox"
                    checked={syncDsn}
                    onChange={e => setSyncDsn(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-neutral-300 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <FileBarChart className="h-5 w-5 text-green-500" />
                      <span className="font-medium text-neutral-900">
                        {t(
                          'vault.sync.dsn',
                          'Déclarations Sociales Nominatives (DSN)'
                        )}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-neutral-500">
                      {t(
                        'vault.sync.dsnDescription',
                        'Déposer la DSN vers le coffre-fort entreprise'
                      )}
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Period */}
            <div>
              <label className="mb-3 block text-sm font-medium text-neutral-700">
                <Calendar className="mr-2 inline h-4 w-4" />
                {t('vault.sync.period', 'Période')}
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-xs text-neutral-600">
                    {t('vault.sync.month', 'Mois')}
                  </label>
                  <select
                    value={selectedMonth}
                    onChange={e => setSelectedMonth(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-neutral-900 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                  >
                    {MONTHS.map(month => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-neutral-600">
                    {t('vault.sync.year', 'Année')}
                  </label>
                  <select
                    value={selectedYear}
                    onChange={e => setSelectedYear(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-neutral-900 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                  >
                    {YEARS.map(year => (
                      <option key={year.value} value={year.value}>
                        {year.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Employee Selection (Individual Mode) */}
            {depositMode === 'individual' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-neutral-700">
                    {t(
                      'vault.manualDeposit.selectEmployees',
                      'Sélection des employés'
                    )}
                  </label>
                  {employeesData?.data && employeesData.data.length > 0 && (
                    <button
                      type="button"
                      onClick={handleSelectAllEmployees}
                      className="flex items-center gap-1.5 rounded-lg border border-purple-300 bg-purple-50 px-3 py-1.5 text-sm font-medium text-purple-700 transition-colors hover:bg-purple-100"
                    >
                      <Users className="h-3.5 w-3.5" />
                      {selectedEmployees.length === employeesData.data.length
                        ? t('common.deselectAll', 'Tout désélectionner')
                        : t('common.selectAll', 'Tout sélectionner')}
                    </button>
                  )}
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                  <input
                    type="text"
                    value={employeeSearch}
                    onChange={e => setEmployeeSearch(e.target.value)}
                    placeholder={t(
                      'vault.manualDeposit.searchEmployees',
                      'Rechercher un employé...'
                    )}
                    className="w-full rounded-lg border border-neutral-300 bg-white py-2 pr-3 pl-10 text-neutral-900 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Employee List */}
                <div className="max-h-60 space-y-2 overflow-y-auto rounded-lg border border-neutral-200 p-2">
                  {isLoadingEmployees ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                    </div>
                  ) : filteredEmployees.length === 0 ? (
                    <div className="py-8 text-center text-sm text-neutral-500">
                      {t(
                        'vault.manualDeposit.noEmployees',
                        'Aucun employé trouvé'
                      )}
                    </div>
                  ) : (
                    filteredEmployees.map((employee: Employee) => {
                      const vaultInfo = getEmployeeVaultInfo(employee.uuid);
                      const vaultActive = isVaultActive(employee.uuid);
                      const employeeHasVault = hasEmployeeVault(employee.uuid);
                      const isPendingActivation =
                        vaultInfo?.vault?.status ===
                          VaultStatus.PENDING_USER_ACTIVATION ||
                        vaultInfo?.vault?.status === VaultStatus.PENDING;

                      return (
                        <label
                          key={employee.uuid}
                          className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-all ${
                            selectedEmployees.includes(employee.uuid)
                              ? vaultActive
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-amber-500 bg-amber-50'
                              : 'border-neutral-200 hover:bg-neutral-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedEmployees.includes(employee.uuid)}
                            onChange={() => handleEmployeeToggle(employee.uuid)}
                            className="h-4 w-4 rounded border-neutral-300 text-purple-600 focus:ring-purple-500"
                          />
                          <div
                            className={`flex h-8 w-8 items-center justify-center rounded-full ${
                              vaultActive
                                ? 'bg-success-100'
                                : isPendingActivation
                                  ? 'bg-amber-100'
                                  : 'bg-neutral-100'
                            }`}
                          >
                            {vaultActive ? (
                              <CheckCircle2 className="text-success-600 h-4 w-4" />
                            ) : isPendingActivation ? (
                              <Clock className="h-4 w-4 text-amber-600" />
                            ) : (
                              <User className="h-4 w-4 text-neutral-500" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-neutral-900">
                              {employee.prenom} {employee.nom}
                            </p>
                            <div className="flex items-center gap-2">
                              {employee.matriculeSalarie && (
                                <span className="text-xs text-neutral-500">
                                  {employee.matriculeSalarie}
                                </span>
                              )}
                              {employeeHasVault && (
                                <span
                                  className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium ${
                                    vaultActive
                                      ? 'bg-success-100 text-success-700'
                                      : 'bg-amber-100 text-amber-700'
                                  }`}
                                >
                                  {vaultActive
                                    ? t('vault.status.active', 'Actif')
                                    : t(
                                        'vault.status.pendingActivation',
                                        'En attente'
                                      )}
                                </span>
                              )}
                              {!employeeHasVault && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-1.5 py-0.5 text-xs font-medium text-neutral-600">
                                  {t('vault.status.noVault', 'Pas de coffre')}
                                </span>
                              )}
                            </div>
                          </div>
                          {selectedEmployees.includes(employee.uuid) &&
                            (vaultActive ? (
                              <CheckCircle2 className="h-5 w-5 text-purple-600" />
                            ) : (
                              <AlertTriangle className="h-5 w-5 text-amber-500" />
                            ))}
                        </label>
                      );
                    })
                  )}
                </div>

                {selectedEmployees.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-neutral-600">
                      {t(
                        'vault.manualDeposit.selectedCount',
                        '{{count}} employé(s) sélectionné(s)',
                        {
                          count: selectedEmployees.length,
                        }
                      )}
                    </p>
                    {selectedWithInactiveVaults.length > 0 && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                          <div className="text-sm text-amber-800">
                            <p className="font-medium">
                              {t(
                                'vault.manualDeposit.inactiveVaultsWarning',
                                "{{count}} employé(s) n'ont pas de coffre-fort actif",
                                {
                                  count: selectedWithInactiveVaults.length,
                                }
                              )}
                            </p>
                            <p className="mt-1 text-amber-700">
                              {t(
                                'vault.manualDeposit.inactiveVaultsHint',
                                'Les documents ne pourront pas être déposés pour ces employés. Assurez-vous que leurs coffres-forts sont initialisés.'
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Warning for inactive vaults (Grouped Mode) */}
            {/* Direct deposit toggle (Grouped mode only) */}
            {depositMode === 'grouped' && syncPayslips && (
              <div className="rounded-lg border border-neutral-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-lg ${directDepositToEmployee ? 'bg-green-100' : 'bg-amber-100'}`}
                    >
                      {directDepositToEmployee ? (
                        <UserCheck className="h-4 w-4 text-green-600" />
                      ) : (
                        <ArrowRightLeft className="h-4 w-4 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <span className="block text-sm font-medium text-neutral-900">
                        {t(
                          'vault.sync.directDeposit',
                          'Dépôt direct aux salariés'
                        )}
                      </span>
                      <span className="block text-xs text-neutral-500">
                        {directDepositToEmployee
                          ? t(
                              'vault.sync.directDepositOnDesc',
                              'Les bulletins vont directement dans les coffres des salariés'
                            )
                          : t(
                              'vault.sync.directDepositOffDesc',
                              'Les bulletins restent dans le coffre employeur pour validation'
                            )}
                      </span>
                    </div>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={directDepositToEmployee}
                      onChange={e =>
                        setDirectDepositToEmployee(e.target.checked)
                      }
                      className="peer sr-only"
                    />
                    <div className="peer h-5 w-9 rounded-full bg-neutral-200 peer-checked:bg-green-500 after:absolute after:top-[2px] after:left-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-neutral-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
                  </label>
                </div>
              </div>
            )}

            {depositMode === 'grouped' &&
              syncPayslips &&
              employeesWithoutActiveVaults.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="text-sm text-amber-800">
                      <p className="mb-1 font-medium">
                        {t(
                          'vault.sync.inactiveVaultsWarningGrouped',
                          "{{count}} employé(s) n'ont pas de coffre-fort actif",
                          {
                            count: employeesWithoutActiveVaults.length,
                          }
                        )}
                      </p>
                      <p className="text-amber-700">
                        {t(
                          'vault.sync.inactiveVaultsHintGrouped',
                          'Les bulletins de paie ne seront déposés que dans les coffres-forts actifs. Les autres employés ne recevront pas leurs documents.'
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

            {/* Info (Grouped Mode) */}
            {depositMode === 'grouped' && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="text-sm text-blue-800">
                    <p className="mb-1 font-medium">
                      {t(
                        'vault.sync.destinationInfo',
                        'Les documents seront envoyés vers :'
                      )}
                    </p>
                    <ul className="list-inside list-disc space-y-1 text-blue-700">
                      <li>
                        <Building2 className="mr-1 inline h-4 w-4" />
                        {t(
                          'vault.sync.folderVaultDestination',
                          'Coffre-fort entreprise'
                        )}{' '}
                        ({folderName})
                      </li>
                      {syncPayslips && (
                        <li>
                          <Users className="mr-1 inline h-4 w-4" />
                          {t(
                            'vault.sync.employeeVaultsDestination',
                            'Coffres-forts employés (si disponibles)'
                          )}
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Sync Button */}
            <Button
              onClick={handleManualSync}
              disabled={
                isSyncing ||
                (!syncPayslips && !syncDsn) ||
                !hasVault ||
                (depositMode === 'individual' && selectedEmployees.length === 0)
              }
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {isSyncing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('vault.manualDeposit.depositing', 'Dépôt en cours...')}
                </>
              ) : (
                <>
                  <Cloud className="mr-2 h-4 w-4" />
                  {t('vault.manualDeposit.depositNow', 'Déposer Maintenant')}
                </>
              )}
            </Button>
          </>
        )}

        {/* ============================================ */}
        {/* Local Upload Mode                            */}
        {/* ============================================ */}
        {sourceMode === 'local' && (
          <>
            {/* Employee Selection (single) */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-neutral-700">
                {t(
                  'vault.localUpload.selectEmployee',
                  "Sélectionner l'employé"
                )}
              </label>

              {/* Search */}
              <div className="relative">
                <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                <input
                  type="text"
                  value={employeeSearch}
                  onChange={e => setEmployeeSearch(e.target.value)}
                  placeholder={t(
                    'vault.manualDeposit.searchEmployees',
                    'Rechercher un employé...'
                  )}
                  className="w-full rounded-lg border border-neutral-300 bg-white py-2 pr-3 pl-10 text-neutral-900 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Employee List (radio) */}
              <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-neutral-200 p-2">
                {isLoadingEmployees ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                  </div>
                ) : filteredEmployees.length === 0 ? (
                  <div className="py-8 text-center text-sm text-neutral-500">
                    {t(
                      'vault.manualDeposit.noEmployees',
                      'Aucun employé trouvé'
                    )}
                  </div>
                ) : (
                  filteredEmployees.map((employee: Employee) => {
                    const vaultInfo = getEmployeeVaultInfo(employee.uuid);
                    const vaultActive = isVaultActive(employee.uuid);
                    const employeeHasVault = hasEmployeeVault(employee.uuid);
                    const isPendingActivation =
                      vaultInfo?.vault?.status ===
                        VaultStatus.PENDING_USER_ACTIVATION ||
                      vaultInfo?.vault?.status === VaultStatus.PENDING;

                    return (
                      <label
                        key={employee.uuid}
                        className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-all ${
                          selectedEmployee === employee.uuid
                            ? vaultActive
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-amber-500 bg-amber-50'
                            : 'border-neutral-200 hover:bg-neutral-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="localEmployee"
                          checked={selectedEmployee === employee.uuid}
                          onChange={() => setSelectedEmployee(employee.uuid)}
                          className="h-4 w-4 border-neutral-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div
                          className={`flex h-8 w-8 items-center justify-center rounded-full ${
                            vaultActive
                              ? 'bg-success-100'
                              : isPendingActivation
                                ? 'bg-amber-100'
                                : 'bg-neutral-100'
                          }`}
                        >
                          {vaultActive ? (
                            <CheckCircle2 className="text-success-600 h-4 w-4" />
                          ) : isPendingActivation ? (
                            <Clock className="h-4 w-4 text-amber-600" />
                          ) : (
                            <User className="h-4 w-4 text-neutral-500" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-neutral-900">
                            {employee.prenom} {employee.nom}
                          </p>
                          <div className="flex items-center gap-2">
                            {employee.matriculeSalarie && (
                              <span className="text-xs text-neutral-500">
                                {employee.matriculeSalarie}
                              </span>
                            )}
                            {employeeHasVault ? (
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium ${
                                  vaultActive
                                    ? 'bg-success-100 text-success-700'
                                    : 'bg-amber-100 text-amber-700'
                                }`}
                              >
                                {vaultActive
                                  ? t('vault.status.active', 'Actif')
                                  : t(
                                      'vault.status.pendingActivation',
                                      'En attente'
                                    )}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-1.5 py-0.5 text-xs font-medium text-neutral-600">
                                {t('vault.status.noVault', 'Pas de coffre')}
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>

              {/* Warning if selected employee has no active vault */}
              {selectedEmployee && !isVaultActive(selectedEmployee) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                    <p className="text-sm text-amber-800">
                      {hasEmployeeVault(selectedEmployee)
                        ? t(
                            'vault.localUpload.vaultNotActive',
                            "Le coffre-fort de cet employé n'est pas encore actif."
                          )
                        : t(
                            'vault.localUpload.noVault',
                            "Cet employé n'a pas de coffre-fort. Veuillez d'abord en créer un dans l'onglet Gestion."
                          )}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Document Type */}
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-700">
                {t('vault.localUpload.documentType', 'Type de document')}
              </label>
              <select
                value={localDocType}
                onChange={e => setLocalDocType(e.target.value)}
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-neutral-900 focus:border-transparent focus:ring-2 focus:ring-purple-500"
              >
                {DOCUMENT_TYPES.map(dt => (
                  <option key={dt.value} value={dt.value}>
                    {t(`vault.documentType.${dt.value}`, dt.label)}
                  </option>
                ))}
              </select>
            </div>

            {/* Period */}
            <div>
              <label className="mb-3 block text-sm font-medium text-neutral-700">
                <Calendar className="mr-2 inline h-4 w-4" />
                {t('vault.sync.period', 'Période')}
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-xs text-neutral-600">
                    {t('vault.sync.month', 'Mois')}
                  </label>
                  <select
                    value={localMonth}
                    onChange={e => setLocalMonth(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-neutral-900 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                  >
                    {MONTHS.map(month => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-neutral-600">
                    {t('vault.sync.year', 'Année')}
                  </label>
                  <select
                    value={localYear}
                    onChange={e => setLocalYear(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-neutral-900 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                  >
                    {YEARS.map(year => (
                      <option key={year.value} value={year.value}>
                        {year.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* File Drop Zone */}
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-700">
                {t('vault.localUpload.file', 'Fichier')}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                className="hidden"
              />

              {!localFile ? (
                <div
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
                    isDragging
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-neutral-300 hover:border-purple-400 hover:bg-neutral-50'
                  }`}
                >
                  <Upload
                    className={`h-10 w-10 ${isDragging ? 'text-purple-500' : 'text-neutral-400'}`}
                  />
                  <p className="mt-3 text-sm font-medium text-neutral-700">
                    {t(
                      'vault.localUpload.dropZone',
                      'Glissez-déposez un fichier ici'
                    )}
                  </p>
                  <p className="mt-1 text-xs text-neutral-500">
                    {t(
                      'vault.localUpload.dropZoneHint',
                      'ou cliquez pour parcourir'
                    )}
                  </p>
                  <p className="mt-2 text-xs text-neutral-400">
                    PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (max 50 MB)
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                    <File className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-neutral-900">
                      {localFile.name}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {formatFileSize(localFile.size)}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setLocalFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-200 hover:text-neutral-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Upload Button */}
            <Button
              onClick={handleLocalUpload}
              disabled={
                isUploading ||
                !localFile ||
                !selectedEmployee ||
                !hasVault ||
                !isVaultActive(selectedEmployee)
              }
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t(
                    'vault.localUpload.uploading',
                    'Téléversement en cours...'
                  )}
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  {t('vault.localUpload.upload', 'Téléverser')}
                </>
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
